import React, { useContext, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ThemeContext } from "../../contexts/ThemeContext.js";
import { ResponsiveContext } from "../../contexts/ResponsiveContext.js";
import { ServerContext } from "../../contexts/ServerContext.js";
import { BounceLoader } from "react-spinners";
import "./sidestatus.scss";

const sidebarVariants = {
  closed: { x: -300 },
  open: { x: 0 },
};

const SideStatus = () => {
  const { isSideStatusOpen, setIsSideStatusOpen } = useContext(ThemeContext);
  const { phoneView } = useContext(ResponsiveContext);

  const { servers, serverInfos, serverPlayers } = useContext(ServerContext);

  const timerRef = useRef(null);

  const handleLeave = () => {
    timerRef.current = setTimeout(() => setIsSideStatusOpen(false), 4000);
  };

  const handleEnter = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isSideStatusOpen && phoneView) {
        setIsSideStatusOpen(false);
      }
    };

    if (isSideStatusOpen && phoneView) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isSideStatusOpen, setIsSideStatusOpen, phoneView]);

  const transition = { duration: 0.35, ease: "easeInOut" };

  const totalPlayers = serverPlayers.reduce(
    (sum, players) => sum + (players?.length || 0),
    0
  );

  return (
    <motion.aside
      className="status-aside"
      variants={sidebarVariants}
      initial="closed"
      animate={isSideStatusOpen ? "open" : "closed"}
      transition={transition}
      onMouseLeave={handleLeave}
      onMouseEnter={handleEnter}
    >
      <div className="sidestatus-wrapper">
        <h3>Server Status</h3>
        <hr />
        <small>
          Gesamte Spieler: <strong>{totalPlayers}</strong>
        </small>
        <hr />

        <div className="server-container">
          {servers.map((server, idx) => {
            const info = serverInfos[idx] || {};

            const statusColor =
              {
                started: "#24d56b",
                online: "#24d56b",
                restarting: "#f5a623",
                starting: "#f5a623",
                updating: "#2e86de",
                stopped: "#e74c3c",
                offline: "#e74c3c",
              }[info.status] || "var(--color)";

            return (
              <div className="server" key={`${server.server ?? server.name}-${idx}`}>
                <h5>Server {idx + 1}</h5>

                <small className="small-status">
                  Status:&nbsp;
                  <span>
                    <BounceLoader size={12} color={statusColor} />
                  </span>
                </small>

                <small>Map: {info?.map || "Syncing.."}</small>

                <small>
                  Spieler: {serverPlayers[idx]?.length || 0}/{info.player_max ?? 0}
                </small>

                {/* letzte 2 HRs weg (wegen flex wrap) */}
                {idx < servers.length - 2 && <hr className="end-hr" />}
              </div>
            );
          })}
        </div>

        <div
          className="open-button"
          onClick={() => setIsSideStatusOpen((prev) => !prev)}
          title={isSideStatusOpen ? "Statusleiste schließen" : "Statusleiste öffnen"}
        >
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 16"
            width="26"
            height="26"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          >
            <motion.line
              x1="5"
              y1="4"
              x2="15"
              y2="4"
              style={{ originX: "50%", originY: "50%" }}
              animate={{
                rotate: isSideStatusOpen ? -45 : 45,
                translateX: isSideStatusOpen ? -1.5 : 0,
              }}
              transition={transition}
            />
            <motion.line
              x1="5"
              y1="12"
              x2="15"
              y2="12"
              style={{ originX: "50%", originY: "50%" }}
              animate={{
                rotate: isSideStatusOpen ? 45 : -45,
                translateX: isSideStatusOpen ? -1.5 : 0,
              }}
              transition={transition}
            />
          </motion.svg>
        </div>
      </div>
    </motion.aside>
  );
};

export default SideStatus;
